import React, { useEffect, useMemo, useState } from "react";
import { listTeams } from "../lib/api";
import { Loader2, Trophy, ArrowDown, ArrowUp } from "lucide-react";

export default function Standings() {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dir, setDir] = useState("desc");

  useEffect(() => {
    listTeams()
      .then(setTeams)
      .finally(() => setLoading(false));
  }, []);

  const sorted = useMemo(() => {
    const arr = [...teams];
    arr.sort((a, b) =>
      dir === "desc" ? b.seeding_points - a.seeding_points : a.seeding_points - b.seeding_points
    );
    return arr;
  }, [teams, dir]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="standings-page">
      <div className="mb-10">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[#FF5E00]/40 bg-[#FF5E00]/5 text-[#FF5E00] text-xs uppercase tracking-[0.25em] font-display mb-4">
          <Trophy className="w-3 h-3" /> Live Standings
        </div>
        <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tight">
          Seeding <span className="text-gradient-orange">Points</span>
        </h1>
        <p className="mt-3 text-white/65 max-w-2xl">
          Top 16 teams by seeding points qualify for the live championship bracket.
        </p>
      </div>

      {loading ? (
        <div className="py-24 flex justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#FF5E00]" />
        </div>
      ) : (
        <div className="glass-card rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-[#1E1233]/80 text-[#A199B8] font-display uppercase text-xs tracking-[0.2em]">
                  <th className="text-left p-4 w-16">#</th>
                  <th className="text-left p-4">Team</th>
                  <th className="text-left p-4 hidden sm:table-cell">Tag</th>
                  <th
                    className="text-right p-4 cursor-pointer select-none hover:text-white"
                    onClick={() => setDir(dir === "desc" ? "asc" : "desc")}
                    data-testid="standings-sort-toggle"
                  >
                    <span className="inline-flex items-center gap-1 justify-end">
                      Points
                      {dir === "desc" ? (
                        <ArrowDown className="w-3 h-3" />
                      ) : (
                        <ArrowUp className="w-3 h-3" />
                      )}
                    </span>
                  </th>
                  <th className="text-right p-4 hidden md:table-cell">Status</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((t, i) => {
                  const rank = dir === "desc" ? i + 1 : sorted.length - i;
                  const top16 = rank <= 16;
                  return (
                    <tr
                      key={t.id}
                      className="border-b border-[#9D4CDD]/10 hover:bg-[#9D4CDD]/8 transition-colors"
                      data-testid={`standings-row-${t.id}`}
                    >
                      <td className="p-4">
                        <span
                          className={`font-display font-black text-lg ${
                            rank === 1
                              ? "text-[#FF5E00]"
                              : rank <= 3
                              ? "text-[#B366F0]"
                              : "text-white/70"
                          }`}
                        >
                          {String(rank).padStart(2, "0")}
                        </span>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          {t.logo && (
                            <img
                              src={t.logo}
                              alt=""
                              className="h-9 w-9 rounded-md object-cover bg-[#1E1233]"
                            />
                          )}
                          <span className="font-display uppercase font-bold tracking-wide">
                            {t.name}
                          </span>
                        </div>
                      </td>
                      <td className="p-4 hidden sm:table-cell font-mono text-xs text-[#FF5E00]">
                        {t.tag}
                      </td>
                      <td className="p-4 text-right font-display font-black text-xl">
                        {t.seeding_points}
                      </td>
                      <td className="p-4 text-right hidden md:table-cell">
                        {top16 ? (
                          <span className="inline-block text-[10px] uppercase tracking-[0.2em] font-display font-bold px-2.5 py-1 rounded bg-[#FF5E00]/15 text-[#FF5E00] border border-[#FF5E00]/40">
                            Qualified
                          </span>
                        ) : (
                          <span className="inline-block text-[10px] uppercase tracking-[0.2em] font-display px-2.5 py-1 rounded bg-white/5 text-white/40 border border-white/10">
                            Eliminated
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
